import React, { Component } from 'react';
import PersonalInfoNewCustomerView from '../views/PersonalInfoNewCustomer.View';
import PersonalInfoExistingCustomerView from '../views/PersonalInfoExistingCustomer.View';
import ContactFormService from '../../services/ContactForm.service';
import ContactFormSubject from '../../models/ContactFormSubject';
import SelectComponent from '../../../../views/components/Select.component';
import ContactFormEntry from '../../models/ContactFormEntry';

import FormError from '../../../../models/FormError';
import InputField from '../../../../views/components/InputField.Component';
import TextArea from '../../../../views/components/TextArea.Component';
import FileInput from '../../../../views/components/FileInput.Component';
import DateInputField from "../../../../views/components/DateInputField.Component";
import ErrorFieldMessage from '../../../../views/components/FieldErrorMessage.Component';
import ContactFormConfiguration from '../../models/ContactFormConfiguration';
import FormValidationService from '../../services/FormValidationService';
import PostalCodeInputField from '../../../../views/components/PostalCodeInputField.Component';
import { UserInfoModel } from '../../models/UserInfo';

type ContactFormPageProps = {
    formID: string,
}

type ContactFormPageState = {
    configuration: ContactFormConfiguration,
    newCustomer: boolean,
    values: ContactFormEntry,
    errors: FormError[],
    translatedErrorMessages: any,
    submitting: boolean,
}

export default class ContactFormPage extends Component<ContactFormPageProps, ContactFormPageState> {

    public contactFormValidator: FormValidationService = new FormValidationService();

    constructor(props: ContactFormPageProps) {
        super(props);

        this.state = {
            configuration: new ContactFormConfiguration(),
            newCustomer: true,
            values: new ContactFormEntry(),
            errors: [],
            translatedErrorMessages: {},
            submitting: false,
        };
    }

    public async componentDidMount() {
        const service: ContactFormService = new ContactFormService();

        const configuration: ContactFormConfiguration = await service.getContactFormConfiguration(this.props.formID);
        const errorMessages: any = await service.getErrorMessageTranslations();

        this.contactFormValidator.translatedErrorMessages = errorMessages;

        const prefillInfo = await this.getPrefillInfo()
        const values = { ...this.state.values }

        if (prefillInfo) {
            values.firstname = prefillInfo.firstName || ''
            values.lastname = prefillInfo.lastName || ''
            values.dateOfBirth = prefillInfo.birthDate ? new Date(prefillInfo.birthDate) : values.dateOfBirth
            values.phone = prefillInfo.phone || ''
            values.email = prefillInfo.email || ''
            values.postalcode = prefillInfo.postalCode || ''
            values.rszNumber = prefillInfo.socialSecurityNumber || ''
            const fileIds = Object.values(prefillInfo.fileIds) || []
            values.customerNumber = fileIds.length ? (fileIds[0] || '') : ''
        }

        this.setState({
            configuration: configuration,
            translatedErrorMessages: errorMessages,
            values: values,
            newCustomer: !values.customerNumber,
        })
    }

    private async setValue(propertyName: string, value: string): Promise<FormError[]> {
        let errors: FormError[] = [];
        const values: any = this.state.values;

        // avoids that a 'space' is a valid required value
        if (value) {
            value = value.trim()
        }

        if (values.hasOwnProperty(propertyName)) {
            values[propertyName] = value;
            errors = await this.validateProperty(values, propertyName);
        }
        else {
            console.error("Error: property " + propertyName + " was not found on the values and could not be filled in correctly");
        }

        this.setState({
            values: values
        });

        return errors;
    }

    private setConsent(value: boolean): void {
        let errors: FormError[] = [];
        const values: ContactFormEntry = this.state.values;

        values.consent = value;

        this.setState({
            values: values
        });
    }

    private async setDateOfBirth(propertyName: string, value: Date): Promise<FormError[]> {

        const values: ContactFormEntry = this.state.values;

        values.dateOfBirth = value;

        const errors = await this.validateProperty(values, "dateOfBirth");

        this.setState({
            values: values
        });

        return errors;
    }

    public toggleNewCustomer(isNewCustomer: boolean) {
        this.setState({
            newCustomer: isNewCustomer
        })
    }

    public setFiles(files: File[]) {
        const values = this.state.values;
        values.files = files;

        this.setState({
            values: values
        });
    }

    public async selectSubject(subject: ContactFormSubject): Promise<FormError[]> {
        const values = this.state.values;

        values.subject = subject;

        const errors = await this.validateProperty(values, "subject");
        this.setState({
            values: values
        })

        return errors
    }

    public async sendForm(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        let errors: FormError[] = await this.contactFormValidator.validate(this.state.values, this.state.newCustomer);

        this.setState({
            errors: errors
        });

        if (errors.length == 0 && !this.state.submitting) {
            this.setState({ submitting: true });
            await new ContactFormService().sendContactForm(this.state.values);
            window.location.href = this.state.configuration.redirectPageURL;
        } else {
            const $errorFields = $("form :input.is-invalid");

            if ($errorFields.length > 0) {
                $errorFields.first().trigger("focus");
            }
        }
    }

    public async validateProperty(values: ContactFormEntry, propertyName: string): Promise<FormError[]> {
        return this.contactFormValidator.validateProperty(values, this.state.newCustomer, propertyName);
    }

    private async getPrefillInfo(): Promise<UserInfoModel | undefined> {
        let token = this.getCookie('PortalInfo')
        let apiUrl = ''

        try {
            token = atob(token || '');
            apiUrl = (document.querySelector('link[rel="portal-api"]') as HTMLLinkElement)?.href
            if (apiUrl.endsWith('/')) {
                apiUrl = apiUrl.slice(0, -1)
            }
        } catch {
            return undefined
        }

        if (!token || !apiUrl) {
            return undefined
        }

        const info: UserInfoModel = await fetch(`${apiUrl}/user/info`, {
            headers: {
                'Authorization': 'Bearer ' + token
            }
        }).then(response => response.json()).catch(() => undefined);

        return info
    }

    private getCookie(name: string) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift();
        } else {
            return undefined
        }
    }

    public render() {
        return (
            <div className="mt-5 mb-5">
                <form>
                    <div className="my-5">
                        <h2>{this.state.configuration.questionTitle}</h2>
                        <div dangerouslySetInnerHTML={{ __html: this.state.configuration.questionDescription }}></div>
                        <hr className="border-brown-25-alpha" />

                        <SelectComponent
                            placeholderText={this.state.configuration.subjectPlaceholder}
                            label={this.state.configuration.subjectLabel}
                            options={this.state.configuration.subjects}
                            onSelected={(subject) => this.selectSubject(subject)}
                            name="subject"
                            id="contact-select-question"
                            errors={this.state.errors} />

                        <TextArea id="question" name="question" label={this.state.configuration.questionLabel} placeholder={this.state.configuration.questionPlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors}></TextArea>
                        <FileInput name="attachements"
                            label={this.state.configuration.attachmentLabel}
                            maxSize={this.state.configuration.attachmentMaxSize}
                            filesToLargeValidationMessage={this.state.configuration.attachmentToLargeValidationMessage}
                            addFilesLabel={this.state.configuration.attachmentAddFileLabel}
                            dragFilesLabel={this.state.configuration.attachmentDragFileLabel}
                            onChange={this.setFiles.bind(this)}
                            errors={this.state.errors}
                            id="attachments"
                            fileUploadAllowedFormatsLabel={this.state.configuration.fileUploadAllowedFormatsLabel}>
                        </FileInput>
                    </div>
                    {
                        (this.state.values.subject && this.state.values.subject.id !== "-1")
                            ? <React.Fragment>
                                <fieldset className="my-5">
                                    <legend className="h2">{this.state.configuration.informationTitle}</legend>
                                    <div dangerouslySetInnerHTML={{ __html: this.state.configuration.informationDescription }}></div>
                                    <hr className="border-brown-25-alhpa" />
                                    {
                                        this.state.values.subject.showClientInfo ?
                                            <div className="mb-2">
                                                <div className="bg-brown-10-alpha p-2 my-4 row">
                                                    <button type="button" className={"btn col-6-md btn-lg w-50" + (this.state.newCustomer ? " btn-primary btn-primary-no-hover" : "")} onClick={() => this.toggleNewCustomer(true)}>
                                                        {this.state.configuration.notYetCustomerButton}
                                                    </button>
                                                    <button type="button" className={"btn col-6-md btn-lg w-50" + (!this.state.newCustomer ? " btn-primary btn-primary-no-hover" : "")} onClick={() => this.toggleNewCustomer(false)}>
                                                        {this.state.configuration.alreadyCustomerButton}
                                                    </button>
                                                </div>
                                                {
                                                    (this.state.newCustomer
                                                        ? <PersonalInfoNewCustomerView onChange={this.setValue.bind(this)} errors={this.state.errors} configuration={this.state.configuration} initialValues={this.state.values} />
                                                        : <PersonalInfoExistingCustomerView onChange={this.setValue.bind(this)} errors={this.state.errors} onDateOfBirthSet={this.setDateOfBirth.bind(this)} configuration={this.state.configuration} initialValues={this.state.values} />)
                                                }
                                            </div>
                                            : null
                                    }
                                    <InputField id="contact-firstname" autocomplete="given-name" name="firstname" label={this.state.configuration.firstnameLabel} placeholder={this.state.configuration.firstnamePlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors} initialValue={this.state.values.firstname}></InputField>
                                    <InputField id="contact-lastname" autocomplete="family-name" name="lastname" label={this.state.configuration.lastnameLabel} placeholder={this.state.configuration.lastnamePlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors} initialValue={this.state.values.lastname}></InputField>
                                    {
                                        this.state.newCustomer &&
                                         <DateInputField id="contact-dateOfBirth" autocomplete="bday" name="dateOfBirth" label={this.state.configuration.dayOfBirthLabel} placeholder={this.state.configuration.dayOfBirthPlaceholder}  onChange={this.setDateOfBirth.bind(this)} errors={this.state.errors} value={this.state.values.dateOfBirth?.toString()}></DateInputField>
                                    }
                                    <InputField id="contact-email" autocomplete="email" name="email" label={this.state.configuration.emailLabel} placeholder={this.state.configuration.emailPlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors} initialValue={this.state.values.email}></InputField>
                                    <InputField id="contact-phone" autocomplete="tel" name="phone" label={this.state.configuration.telephoneLabel} placeholder={this.state.configuration.telephonePlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors} initialValue={this.state.values.phone}></InputField>
                                    <PostalCodeInputField id="contact-postalcode" name="postalcode" label={this.state.configuration.postalCodeLabel} placeholder={this.state.configuration.postalCodePlaceholder} onChange={this.setValue.bind(this)} errors={this.state.errors} initialValue={this.state.values.postalcode}></PostalCodeInputField>
                                    <div className="my-5">
                                        <div className="d-flex bg-brown-10-alpha p-2">
                                            <div className="custom-control custom-checkbox my-2">
                                                <input className="custom-control-input"
                                                    type="checkbox" id="consent" name="consent" onChange={(event: React.ChangeEvent<HTMLInputElement>) => { this.setConsent(event.target.checked) }}
                                                />
                                                <label className="custom-control-label font-weight-normal" htmlFor="consent"><span dangerouslySetInnerHTML={{ __html: this.state.configuration.consentLabel }}></span> <a href={this.state.configuration.privacyPageURL} target="_blank">{this.state.configuration.privacyPageLabel}</a>.</label>
                                            </div>
                                        </div>
                                        <ErrorFieldMessage name="consent" errors={this.state.errors}></ErrorFieldMessage>
                                    </div>
                                </fieldset>
                            </React.Fragment>
                            : null
                    }
                    <div className='d-flex justify-content-end'>
                        <button type="submit" className="btn btn-primary" onClick={(e) => { this.sendForm(e); }} disabled={this.state.submitting}>{this.state.configuration.sendButton}</button>
                    </div>
                </form>
            </div>
        );
    }
}